/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  ContactFinderDialogConfig,
  contactFinderDialogRoute,
  ContactFinderListItem,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents, ContactDeliveryMethod, ContactType, SystemRoleBit, UserSystemRole } from '@gh/builder-shared-data';
import { buildOptionSetFromDictionary, createDefaultGridModel } from '@gh/common-internal-shared';
import { ActionFlow, EnumDictionary, getAllNewSelectedEntities, getRouteInitData } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  noAnswer,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildEditableColumn,
  buildEditableSelectColumn,
  caseInsensitiveGridComparator,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';

import { map } from 'lodash';

import { SchedulingSupplierListBusinessService } from '../../resources/scheduling-supplier-list.business.service';
import {
  createFromContactFinderList,
  getSchedulingSupplierListItemId,
  SCHEDULING_SUPPLIER_LIST_INIT,
  SchedulingSupervisorAutocomplete,
  SchedulingSupplierListDetails,
  SchedulingSupplierListDetailsUpdate,
  schedulingSupplierListFieldState,
  SchedulingSupplierListItem,
} from '../../resources/scheduling-supplier-list.model';
import { SchedulingSupplierListFormState } from './scheduling-supplier-list-form.service';

export type SchedulingSupervisorDetailsSaveFlowState = FormFlowState<SchedulingSupplierListDetails>
  & { saveResponse: SchedulingSupplierListItem[] };

@Component({
  selector: 'gh-scheduling-supervisor-tab',
  template: require('./scheduling-supplier-list-tab.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    SchedulingSupplierListFormState,
  ],
})
export class SchedulingSupplierListTabComponent extends AbstractFormComponent<SchedulingSupplierListDetails>
  implements OnInit {

  gridDefinition: GridDefinition;
  supervisor: SchedulingSupplierListDetails;
  contactDeliveryMethod: EnumDictionary<ContactDeliveryMethod>;

  constructor(private route: ActivatedRoute,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: SchedulingSupplierListBusinessService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private userContext: BuilderUserContext,
              public form: SchedulingSupplierListFormState) {
    super();
  }

  get completeFlow(): ActionFlow<SchedulingSupervisorDetailsSaveFlowState> {
    return this.pipelines.complete<SchedulingSupervisorDetailsSaveFlowState>({
      validateForm: this.commonActions.validateWithEffect(
        () => this.isSupplierListInvalid(),
        () => this.notificationService.error('msg_he_incorrect_data_in_tables')),
      execute: this.commonActions.async((state) =>
        this.businessService.updateSupervisorSuppliers(<SchedulingSupplierListDetailsUpdate>this.form.value).pipe(
          map$((saveResponse: SchedulingSupplierListItem[]) => ({ ...state, saveResponse })))),
      finish: ({ form, saveResponse }) =>
        this.form.refreshCurrentSupervisor(form.submitValue.supervisor, saveResponse, saveResponse),
    });
  }

  ngOnInit(): void {
    const { contactDeliveryMethod, supplierList } = getRouteInitData(this.route);
    this.contactDeliveryMethod = contactDeliveryMethod;
    this.form.build(SCHEDULING_SUPPLIER_LIST_INIT);

    if (this.isUserSupervisor()) {
      const { fullName: userName, id: userId } = this.userContext.data;
      this.refreshCurrentSupervisor({
        supervisor: { userName, userId },
        suppliers: supplierList,
        originalSuppliers: supplierList,
      });
    }

    this.gridDefinition = this.buildGridDefinition();
  }

  loadSupervisorList($event: AutocompleteLoadMoreEvent): void {
    $event.load(this.businessService.getAutocompleteSupervisorUser({
      ...$event.query,
      filter: {
        name: $event.query.filter,
        showInactive: false,
        officeRole: this.userContext.hasSystemRole(SystemRoleBit.Supervisor) ? void 0 : UserSystemRole.Supervisor,
      },
    }));
  }

  onSupervisorSelected(supervisor: Maybe<SchedulingSupervisorAutocomplete>): void {
    if (supervisor) {
      if (this.form.dirty) {
        const confirmation$ = this.dialogService.confirmation(yesNoConfirmation('msg_unsaved_info'));

        confirmation$.pipe(
          filter$(yesAnswer),
          switchMap$(() => this.businessService.updateSupervisorSuppliers(<SchedulingSupplierListDetailsUpdate>this.form.value).pipe(
            (this.waitingService.operator()))),
          switchMap$(() => this.businessService.findAllSuppliers(supervisor.userId).pipe(
            (this.waitingService.operator()))))
          .subscribe(
            (suppliers: SchedulingSupplierListItem[]) =>
              this.refreshCurrentSupervisor({ supervisor, suppliers, originalSuppliers: suppliers }),
            this.notificationService.processAll);

        confirmation$.pipe(
          filter$(noAnswer),
          switchMap$(() => this.businessService.findAllSuppliers(supervisor.userId).pipe(
            (this.waitingService.operator()))))
          .subscribe(
            (suppliers: SchedulingSupplierListItem[]) =>
              this.refreshCurrentSupervisor({ supervisor, suppliers, originalSuppliers: suppliers }),
            this.notificationService.processAll);
      } else {
        this.businessService.findAllSuppliers(supervisor.userId).pipe(
          (this.waitingService.operator()))
          .subscribe(
            (suppliers: SchedulingSupplierListItem[]) =>
              this.refreshCurrentSupervisor({ supervisor, suppliers, originalSuppliers: suppliers }),
            this.notificationService.processAll);
      }
    } else {
      this.form.reset();
    }
  }

  onDelete(supplier: SchedulingSupplierListItem): void {
    this.dialogService.confirmation(yesCancelConfirmation('msg_subbie_supplier_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.markSupplierAsDeleted(supplier));
  }

  onSupplierAdd(): void {
    this.dialogService.openWithInput(contactFinderDialogRoute, <ContactFinderDialogConfig>{
      type: ContactType.CreditorSupplier,
      withAddress: true,
      preselected: map(this.form.preselectedSuppliers,
                       ({ supplierContactId }: SchedulingSupplierListItem) => supplierContactId),
      multiselection: true,
      selection: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((contactList: ContactFinderListItem[]) => {
        const supplierList = map(contactList, createFromContactFinderList);
        this.form.addSupplierList(supplierList);
      }, this.notificationService.processAll); // tslint:disable-line:align
  }

  onReset(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.resetSuppliers());
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.SubbiesSuppliers.Edited],
      label: 'lbl_menu_sub_contractors_suppliers',
    });
  }

  isUserSupervisor(): boolean {
    return !this.isUserFranchisee() && this.userContext.hasSystemRole(SystemRoleBit.Supervisor);
  }

  isUserFranchisee(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Franchisee)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Corporate);
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      editable: this.form.editable,
      getId: getSchedulingSupplierListItemId,
      getCellDef: schedulingSupplierListFieldState,
      command: (entity: SchedulingSupplierListItem, name: keyof SchedulingSupplierListItem, value) =>
        this.form.updateCurrentSupplier(entity, name, value),
    });

    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        field: 'name',
        width: 300,
        comparator: caseInsensitiveGridComparator,
      }, {
        field: 'sendBy',
        width: 150,
        sortable: false,
        ...buildEditableSelectColumn(
          buildOptionSetFromDictionary(this.contactDeliveryMethod),
          model.getCellAccessor('sendBy'),
        ),
      }, {
        field: 'supplierAddress',
        width: 300,
        editable: false,
        ...buildEditableColumn(model.getCellAccessor('supplierAddress'), {}),
      }],
    });
  }

  private refreshCurrentSupervisor({ supervisor, suppliers, originalSuppliers }: SchedulingSupplierListDetails): void {
    this.form.refreshCurrentSupervisor(supervisor, suppliers, originalSuppliers);
  }

  private isSupplierListInvalid(): boolean {
    return this.form.isSupplierListInvalid();
  }
}
